import { Stack } from '@mantine/core';
import { can } from '@sportybot-web/auth/permissions/permissions';
import { AdhocCarousel } from '@sportybot-web/features/home/components/adhoc-carousel/adhoc-carousel';
import { DatePicker } from '@sportybot-web/features/home/components/date-picker/date.picker';
import { Schedule } from '@sportybot-web/features/home/components/schedule/schedule';
import {
  type HomeParams,
  homeParamsSchema,
} from '@sportybot-web/features/home/schemas/home-params';
import { DEFAULT_SPORT_ID } from '@sportybot-web/services/stats-api/constants';
import { Sport } from '@sportybot-web/services/types';
import { isMILBSport } from '@sportybot-web/services/utils';
import { formatScheduleDate } from '@sportybot-web/utils/date';
import { useIsFetching } from '@tanstack/react-query';
import {
  createFileRoute,
  redirect,
  stripSearchParams,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_authenticated/')({
  validateSearch: homeParamsSchema,
  component: RouteComponent,
  search: {
    middlewares: [
      stripSearchParams({
        get date() {
          return formatScheduleDate();
        },
        sportId: DEFAULT_SPORT_ID,
      }),
    ],
  },
  beforeLoad: ({ context: { user }, search }) => {
    if (!can(user, 'CAN_VIEW_ALL_SPORTS') && !isMILBSport(search.sportId)) {
      throw redirect({
        to: '.',
        replace: true,
        search: (current) => ({ ...current, sportId: Sport.AAA }),
      });
    }
  },
});
/* eslint-enable perfectionist/sort-objects */

function RouteComponent() {
  const { date, sportId } = useSearch({ from: Route.id });
  const navigate = useNavigate({ from: Route.fullPath });

  const isFetching = useIsFetching({ queryKey: ['schedule'] });

  const handleChangeSearchParams = (
    key: keyof HomeParams,
    value: HomeParams[keyof HomeParams] | null,
  ) => {
    void navigate({
      replace: true,
      search: (current) => ({ ...current, [key]: value }),
    });
  };

  return (
    <Stack gap="space400">
      <DatePicker
        loading={isFetching > 0}
        onChange={(date) => handleChangeSearchParams('date', date)}
        value={date}
      />
      <Stack gap="space600">
        <Schedule
          date={date}
          onChangeSportId={(value) => handleChangeSearchParams('sportId', value)}
          onViewTodayGames={() => handleChangeSearchParams('date', formatScheduleDate())}
          sportId={sportId}
        />
        <AdhocCarousel date={date} />
      </Stack>
    </Stack>
  );
}
